import {
  integrationLevelSchema,
  saasEnvironmentSchema,
  saasIdentityModeSchema,
  saasProductStatusSchema,
  saasProductTypeSchema,
  type CreateSaasProductDto,
  type IntegrationLevelSchema,
  type QuerySaasProductsDto,
  type SaasEnvironmentSchema,
  type SaasIdentityModeSchema,
  type SaasProductStatusSchema,
  type SaasProductTypeSchema,
  type UpdateSaasProductDto,
} from '@control-center/shared';
import { z } from 'zod';

import { apiClient } from './api-client';

// =============================================================================
// Response schemas — Story qa-hardening 2026-05-28.
// Cada fetch valida o shape do que o backend devolveu. Bug de contract
// (campo renomeado, snake/camel divergente, type mudou) estoura RUNTIME
// na primeira chamada — não fica oculto até alguém clicar.
// =============================================================================

// `.optional()` tolera backend antigo que não conhece o campo (até reiniciar).
// `.nullable()` cobre o caso "novo backend, mas SaaS nunca foi checado".
const lastHealthCheckSchema = z
  .object({
    healthy: z.boolean(),
    checkedAt: z.string(),
    httpStatus: z.number().nullable(),
    responseTimeMs: z.number().nullable(),
    errorMessage: z.string().nullable(),
  })
  .nullable()
  .optional();

const saasProductEntitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  descriptionShort: z.string().nullable(),
  descriptionLong: z.string().nullable(),
  type: saasProductTypeSchema,
  status: saasProductStatusSchema,
  url: z.string().nullable(),
  adminUrl: z.string().nullable(),
  emailLogoUrl: z.string().nullable(),
  subscriptionUrl: z.string().nullable(),
  environment: saasEnvironmentSchema,
  technicalUserId: z.string().nullable(),
  commercialUserId: z.string().nullable(),
  observations: z.string().nullable(),
  integrationLevel: integrationLevelSchema,
  // Peça 3 — como o cliente entra na compra. A API sobe ANTES da tela.
  identityMode: saasIdentityModeSchema,
  asaasAccountId: z.string().nullable(),
  apiUrl: z.string().nullable(),
  rateLimitPerMin: z.number(),
  // Fase 4 (12.2) — override por SaaS da suspensão automática.
  autoSuspensionEnabled: z.boolean().optional().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
  hasBootstrapApiKey: z.boolean(),
  hasWebhookSecret: z.boolean(),
  lastHealthCheck: lastHealthCheckSchema,
});

const listMetaSchema = z.object({
  next_cursor: z.string().nullable(),
  total_estimate: z.number().nullable(),
});

const listResponseSchema = z.object({
  items: z.array(saasProductEntitySchema),
  meta: listMetaSchema,
});

const singleResponseSchema = z.object({ data: saasProductEntitySchema });

/**
 * View pública do SaaS Product (vinda da API). Sem `bootstrapApiKey` nem `webhookSecret`
 * em claro — só as flags booleanas que indicam se o secret está definido.
 */
export interface LastHealthCheck {
  healthy: boolean;
  checkedAt: string; // ISO
  httpStatus: number | null;
  responseTimeMs: number | null;
  errorMessage: string | null;
}

export interface SaasProductEntity {
  id: string;
  name: string;
  slug: string;
  descriptionShort: string | null;
  descriptionLong: string | null;
  type: SaasProductTypeSchema;
  status: SaasProductStatusSchema;
  url: string | null;
  adminUrl: string | null;
  /** Logo do cabecalho dos e-mails deste produto. */
  emailLogoUrl: string | null;
  subscriptionUrl: string | null;
  environment: SaasEnvironmentSchema;
  technicalUserId: string | null;
  commercialUserId: string | null;
  observations: string | null;
  integrationLevel: IntegrationLevelSchema;
  /** Como o cliente entra na compra (peça 3): e-mail ou login do sistema. */
  identityMode: SaasIdentityModeSchema;
  asaasAccountId: string | null;
  apiUrl: string | null;
  rateLimitPerMin: number;
  autoSuspensionEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  hasBootstrapApiKey: boolean;
  hasWebhookSecret: boolean;
  lastHealthCheck?: LastHealthCheck | null;
}

interface ListResponse {
  items: SaasProductEntity[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

const HEALTH_FRESHNESS_MS = 10 * 60 * 1000; // 10 minutos

/**
 * Um SaaS está "Conectado" quando todos esses critérios são satisfeitos:
 *  1. Config OK            → webhookSecret + apiUrl + integrationLevel ≠ manual
 *  2. Último health check  → healthy=true (cron bate /health a cada 5min)
 *  3. Recência             → checkedAt < 10min atrás (Nível 3 do badge)
 *
 * Sem item 2/3, o SaaS pode estar "configurado" mas com o servidor caído —
 * o badge reflete a realidade runtime, não só a config.
 */
export function isSaasConnected(saas: {
  hasWebhookSecret: boolean;
  apiUrl: string | null;
  integrationLevel: IntegrationLevelSchema;
  lastHealthCheck?: LastHealthCheck | null;
}): boolean {
  const configOk =
    saas.hasWebhookSecret &&
    Boolean(saas.apiUrl && saas.apiUrl.trim().length > 0) &&
    saas.integrationLevel !== 'manual';
  if (!configOk) return false;

  if (!saas.lastHealthCheck) return false;
  if (!saas.lastHealthCheck.healthy) return false;

  const ageMs = Date.now() - new Date(saas.lastHealthCheck.checkedAt).getTime();
  return ageMs < HEALTH_FRESHNESS_MS;
}

/** Útil pra exibir motivo do "Desconectado" no UI. */
export function diagnoseSaasConnection(saas: {
  hasWebhookSecret: boolean;
  apiUrl: string | null;
  integrationLevel: IntegrationLevelSchema;
  lastHealthCheck?: LastHealthCheck | null;
}):
  | { status: 'connected' }
  | { status: 'no_config'; reasons: string[] }
  | { status: 'never_checked' }
  | { status: 'unhealthy'; httpStatus: number | null; errorMessage: string | null }
  | { status: 'stale'; ageMs: number } {
  const reasons: string[] = [];
  if (!saas.hasWebhookSecret) reasons.push('Falta chave de segurança');
  if (!saas.apiUrl) reasons.push('Falta endereço da API');
  if (saas.integrationLevel === 'manual') reasons.push('Integração marcada como manual');
  if (reasons.length > 0) return { status: 'no_config', reasons };

  if (!saas.lastHealthCheck) return { status: 'never_checked' };
  if (!saas.lastHealthCheck.healthy) {
    return {
      status: 'unhealthy',
      httpStatus: saas.lastHealthCheck.httpStatus,
      errorMessage: saas.lastHealthCheck.errorMessage,
    };
  }
  const ageMs = Date.now() - new Date(saas.lastHealthCheck.checkedAt).getTime();
  if (ageMs >= HEALTH_FRESHNESS_MS) return { status: 'stale', ageMs };
  return { status: 'connected' };
}

export async function fetchSaasProducts(
  params: QuerySaasProductsDto = { limit: 20 },
): Promise<ListResponse> {
  const { data } = await apiClient.get('/api/saas-products', { params });
  return listResponseSchema.parse(data);
}

export async function fetchSaasProduct(id: string): Promise<SaasProductEntity> {
  const { data } = await apiClient.get(`/api/saas-products/${id}`);
  return singleResponseSchema.parse(data).data;
}

export async function createSaasProduct(payload: CreateSaasProductDto): Promise<SaasProductEntity> {
  const { data } = await apiClient.post('/api/saas-products', payload);
  return singleResponseSchema.parse(data).data;
}

export async function updateSaasProduct(
  id: string,
  payload: UpdateSaasProductDto,
): Promise<SaasProductEntity> {
  const { data } = await apiClient.patch(`/api/saas-products/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function deleteSaasProduct(id: string, version: number): Promise<void> {
  await apiClient.delete(`/api/saas-products/${id}`, { data: { version } });
}

const healthCheckResultSchema = z.object({
  data: z.object({
    healthy: z.boolean(),
    httpStatus: z.number().nullable(),
    responseTimeMs: z.number().nullable(),
    errorMessage: z.string().nullable(),
  }),
});

export type HealthCheckResult = z.infer<typeof healthCheckResultSchema>['data'];

/** Trigger manual de health check. Admin only. Útil pra testar setup novo. */
export async function runSaasHealthCheck(id: string): Promise<HealthCheckResult> {
  const { data } = await apiClient.post(`/api/saas-products/${id}/health-check`);
  return healthCheckResultSchema.parse(data).data;
}

const importReportSchema = z.object({
  data: z.object({
    saasSlug: z.string(),
    usersReceived: z.number(),
    clientsCreated: z.number(),
    clientsLinked: z.number(),
    alreadyLinked: z.number(),
    subscriptionsCreated: z.number(),
    subscriptionsSkipped: z.number(),
    errors: z.array(z.string()),
  }),
});

export type SaasImportReport = z.infer<typeof importReportSchema>['data'];

/** Bootstrap real: espelha users+companies+subscriptions do SaaS pro CC. Idempotente. */
export async function runSaasImport(id: string): Promise<SaasImportReport> {
  const { data } = await apiClient.post(`/api/saas-products/${id}/import`);
  return importReportSchema.parse(data).data;
}
